/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, IconButton, Box, DialogContent, Alert, DialogActions, Button, TextField, Typography } from '@mui/material';
import { IoClose } from "react-icons/io5";
import { ScrollableDialogContent } from '../Components/ScrollableDialogContent.js';
import { SelectClassification } from '../Utils/classification_helper.js';

const AddDatasetPopup = ( { triggerDatasetPopup, setTriggerDatasetPopup, loadDatasets, default_data, getToken }) => {
  const [modalIsOpen, setIsOpen] = React.useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dataset, setDataset] = useState('');
  const [classification, setClassification] = useState(default_data.default_allowed_classifications[0]);

  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  function closeModal() {
    setTriggerDatasetPopup(0);
    setDataset('')
    setSuccessMessage('')
    setErrorMessage('')
    setIsOpen(false);
  }

  useEffect(() => {
    if (triggerDatasetPopup) {
      if (triggerDatasetPopup === 1) {
        setIsOpen(true)
      }
    }
  }, [triggerDatasetPopup]);

  function handleSubmit() {
    setErrorMessage('');
    setSuccessMessage('');

    var token = getToken()
    if(token === false) {
      return;
    }

    if(dataset === null || dataset === '') {
      setErrorMessage('Please enter a dataset name. It can only contain alphanumeric characters and - only.')
      return;
    }
    if(dataset.length > 100) {
      setErrorMessage('A dataset name cannot be more than 100 characters.')
      return;
    }
    
    setIsLoading(true)

    try {
      fetch(default_data.default_user_service_url_no_cac + '/add-dataset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-tokens': `${token}`
        },        
        body: JSON.stringify({
          dataset: dataset,
          classification: classification
        })
      })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while getting data.');
      })
      .then(data => {
        var ret = ''
        if(data['status'] === 200) {
          ret = "Dataset created!"
        }
        else if(data['status'] === 404) {
          ret = "Sorry, you have hit the limit of datasets you can create (150), please try again or reach out to our support team for help."
        }
        else if(data['status'] === 405) {
          ret = "Sorry, this dataset already exists."
        }
        else if(data['status'] === 406) {
          ret = "Sorry, this dataset name is too long and must be under 100 characters."
        }
        else if(data['status'] === 407) {
          ret =  "Sorry, you need to logout and login with a CAC/PIV to create a CUI dataset."
        }
        else if(data['status'] === 408) {
          ret = "Sorry, your dataset name must be more than 3 characters."
        }
        else if(data['status'] === 409) {
          ret = "Sorry, your dataset name must only be alphanumerical with '-' accepted. Spaces are NOT allowed."
        }
        else {
          ret = "Sorry, internal error, please try again or reach out to our support team for help."
        }
        if(data['status'] === 200) {
          setSuccessMessage(ret)
          loadDatasets()
          setErrorMessage('')
        }
        else {
          setErrorMessage(ret)
          setSuccessMessage('')
        }
        setIsLoading(false)
      })       
      .catch(error => {
        setErrorMessage('Internal error, please contact the customer support.')
        setIsLoading(false)
      });      
    } catch (error) {
      setErrorMessage('Internal error, please contact the customer support.')
      setIsLoading(false)
    }          
  } 

  const handleChangeClassification = (event) => {
    setClassification(event.target.value);
  }

  return (
    <Dialog
      open={modalIsOpen}
      onClose={closeModal}
    >
      <DialogTitle>
        Create a Dataset (Data Collection)
      </DialogTitle>
      <ScrollableDialogContent modalIsOpen={modalIsOpen}>
        <Alert severity="info" sx={{ mb: 3 }}>
          NOTE: think of a Dataset like a folder, it can contains various documents and data and you can assign read access to other users in your organization.
        </Alert>
        <Typography mb={1}>Dataset name (only alphanumeric characters and - allowed. NO spaces!): </Typography>
        <TextField
          className="dataset-input"
          value={dataset}
          onChange={(e)=> setDataset(e.target.value)}
          size='small'
          fullWidth
          placeholder="Enter a dataset name"
          error={dataset.includes(' ')}
          helperText={dataset.includes(' ') ? 'No spaces allowed' : ''}
        />
        <div style={{marginTop: '10px'}}>
          <SelectClassification value={classification} onChange={handleChangeClassification} default_data={default_data} />
        </div>
        {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
        {successMessage && <p style={{ color: 'green' }}>{successMessage}</p>}
      </ScrollableDialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} disabled={isLoading || dataset.includes(' ')} variant='contained'>
          Create Dataset
        </Button>
        <Button onClick={closeModal} color='error'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddDatasetPopup;
